import { CartAddEvent } from '@theme/events';

/**
 * `shop-sdk.js` intercepts cart Ajax and emits DOM events. Horizon listens for `CartAddEvent` on
 * `cart:update` (`ThemeEvents.cartUpdate`).
 *
 * **Do not listen to `cart:all`.** Shop SDK fires `cart:all` after every `POST /cart/change.js` (qty
 * updates, line removal). Bridging that to `CartAddEvent` re-enters `cart-items-component` while it
 * is already morphing from its own `CartUpdateEvent`, which races `sectionRenderer.renderSection`
 * and breaks drawer UI (remove button, row state).
 *
 * **`cart:add`** is emitted only for add-to-cart flows (`POST /cart/add.js`, form `POST /cart/add`,
 * etc.) — not for change/remove — so we only sync the drawer when new merchandise hits the cart
 * (e.g. Shogun). We still `GET /cart.js` because `cart:add` detail is line payload, not guaranteed
 * `item_count` shape.
 *
 * **Rate limiting:** Shop SDK already calls `GET /cart.js` right after a successful add (before
 * `cart:all`). This bridge issues a second `GET /cart.js` for the same add. Each `CartAddEvent`
 * without `sections` in `detail` makes `cart-items-component` call `sectionRenderer.renderSection`
 * (another storefront GET). Rapid `cart:add` (double-submit, Shogun, tests) multiplies that.
 * We **debounce** sync so bursts collapse to one fetch + one `cart:update` per quiet window.
 */
const SOURCE = 'shop-sdk-horizon-cart-bridge';
const DEBOUNCE_MS = 400;

/** @type {ReturnType<typeof setTimeout> | undefined} */
let debounceTimer;

async function runHorizonCartSync() {
  try {
    const res = await fetch('/cart.js');
    if (!res.ok) return;
    const cart = await res.json();
    document.dispatchEvent(
      new CartAddEvent(cart, SOURCE, {
        source: SOURCE,
        itemCount: cart.item_count,
      })
    );
  } catch {
    /* non-fatal */
  }
}

function scheduleHorizonCartSync() {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(runHorizonCartSync, DEBOUNCE_MS);
}

document.addEventListener('cart:add', scheduleHorizonCartSync);
